import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import { configure, GlobalHotKeys } from "react-hotkeys";
import { FormControlLabel, Switch, Typography } from "@material-ui/core";
import { SecondaryButton } from "@prestosports/commons-ui";
import Toolbar from "@material-ui/core/Toolbar";
import { PlayerStatus } from "@prestosports/prestostats-core/Common";
import { ParticipationStyle } from "@prestosports/prestostats-core/Football";
import PlayerButton from "../../../player/PlayerButton";
import PlayerFormat from "../../../../util/player-format";
import gameButtonGridStyles from "../../styles/GameButtonGrid.styles";
import { usePlayersFootballContext } from "../../../../state/players/football-context";
import { useGameContextContext } from "../../../../state/game-context/context";
import { useEventContext } from "../../../../state/events/context";
import { useTeamsContext } from "../../../../state/teams/context";

const UniformKeyListener = ({
	players,
	onMatch,
	disabled = false,
	isOptional = false,
	isEditing = false,
	selectedPlayers = []
}) => {
	configure({
		allowCombinationSubmatches: true
	});

	const [currentSequence, setCurrentSequence] = useState("");

	const keyMap = {
		SUBMIT: ["enter"],
		UNIFORM: {
			sequences: [..."abcdefghijklmnopqrstuvwxyz0123456789"]
		},
		CLEAR: ["backspace", "del"],
		SKIP: ["tab"]
	};
	const handlers = {
		SUBMIT: () => {
			const player = players
				.filter(
					(p) =>
						p.active ||
						!p.playerId ||
						[PlayerFormat.TeamUniform, PlayerFormat.NotSpecifiedUniform].some(
							(u) => u === p.uniform
						)
				)
				.find(
					(p) => PlayerFormat.getUniformNormalized(p.uniform) === currentSequence
				);
			if (player && onMatch) {
				onMatch(player);
			}
			setCurrentSequence("");
		},
		UNIFORM: (event) => {
			const sequence = currentSequence + event.key.toUpperCase();
			if (sequence.length === 3) {
				const player = players
					.filter((p) => p.active || !p.playerId)
					.find((p) => PlayerFormat.getUniformNormalized(p.uniform) === sequence);
				if (player && onMatch) {
					onMatch(player);
				}
				setCurrentSequence("");
			} else {
				setCurrentSequence(sequence);
			}
		},
		CLEAR: () => {
			setCurrentSequence("");
		},
		SKIP: (e) => {
			e.preventDefault();
			const hasValidSelection = Array.isArray(selectedPlayers)
				? selectedPlayers.length
				: selectedPlayers.id;
			if (!isEditing && (isOptional || hasValidSelection)) {
				onMatch({});
			}
		}
	};

	return (
		<>
			<Typography variant="inherit">{`Please enter the uniform: ${currentSequence}...`}</Typography>
			{!disabled && (
				<GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />
			)}
		</>
	);
};

const PlayerSelector = ({
	disableKeyListener,
	team,
	selected = [],
	onSelected,
	maxAllowed = 1,
	isMultiselect = false,
	editingPlay = false,
	optional = false,
	positionPref = [],
	defaultValue
}) => {
	const classes = gameButtonGridStyles();

	const { state: footballPlayersContextState } = usePlayersFootballContext({
		team
	});

	const { state: eventState } = useEventContext();
	const { gameConfiguration } = eventState.currentEventSettings() || {};
	const { participationStyle } = gameConfiguration?.general || {};

	const { state, orderPlayersByPosition } = useGameContextContext();
	const orderingPlayersByPosition = state.orderPlayersByPosition();

	const { state: teamsState } = useTeamsContext();
	const teamColor = teamsState.teamColor(team);

	const orderPlayers = (players = []) => {
		const orderedCodes = positionPref.map(({ code }) => code) || [];
		let sortedPlayers = [];

		if (orderingPlayersByPosition) {
			orderedCodes.forEach((code) => {
				sortedPlayers = sortedPlayers.concat(
					players.filter(({ positions }) => positions?.[0]?.code === code)
				);
			});
		}

		sortedPlayers = sortedPlayers.concat(
			players.filter(
				({ id }) => !sortedPlayers.some(({ id: spId }) => spId === id)
			)
		);
		return sortedPlayers;
	};

	const players = orderPlayers(
		participationStyle === ParticipationStyle.Continuous.code
			? footballPlayersContextState.activePlayers()
			: footballPlayersContextState.participatingPlayers()
	);

	let currentPlayers = [];
	if (selected) {
		currentPlayers = Array.isArray(selected) ? selected : [selected];
		currentPlayers = currentPlayers.filter((p) => p.id);
	}

	const onPlayerSelected = (maxReached, skipped) => {
		onSelected(
			isMultiselect ? currentPlayers : currentPlayers[0] || {},
			maxReached,
			skipped
		);
	};

	const clear = () => {
		currentPlayers = [];
		onPlayerSelected(false, false);
	};

	const selectPlayer = (player) => {
		let maxReached = true;
		const skipped = !player?.id;

		if (!skipped) {
			if (isMultiselect) {
				if (maxAllowed > 1) {
					const currentlySelectedIndex = currentPlayers.findIndex(
						(p) => p.id === player.id
					);
					if (currentlySelectedIndex >= 0) {
						currentPlayers.splice(currentlySelectedIndex, 1);
						maxReached = false;
					} else {
						currentPlayers = [...currentPlayers, player];
						maxReached = currentPlayers.length === maxAllowed;
					}
				} else {
					currentPlayers = [player];
				}
			} else {
				currentPlayers = [player];
			}
		}

		onPlayerSelected(maxReached, skipped);
	};

	const canContinue = () =>
		!editingPlay && !!selected.length && !!defaultValue?.length;

	const canSkip = () => {
		return optional || (isMultiselect && maxAllowed > 1 && !editingPlay);
	};

	const canClear = () => {
		return optional || (isMultiselect && maxAllowed > 1 && editingPlay);
	};

	const selectionDisabled =
		isMultiselect && maxAllowed > 1 && currentPlayers?.length >= maxAllowed;

	return (
		<>
			{!!positionPref.length && (
				<Grid container className={classes.playerPositionToggle}>
					<FormControlLabel
						control={
							<Switch
								id="order-by-position-switch"
								color="primary"
								checked={!!orderingPlayersByPosition}
								onChange={(e) => orderPlayersByPosition(e.target.checked)}
							/>
						}
						label="Order by Position?"
						labelPlacement="end"
					/>
				</Grid>
			)}

			<Grid container>
				{players &&
					players
						.filter((p) => !PlayerStatus.getStatusInactive(p.playerStatus))
						.map((player) => {
							const isSelected = !!currentPlayers.find((p) => p.id === player.id);
							return (
								<Grid item key={player.id} className={classes.gridItem} align="center">
									<PlayerButton
										player={player}
										onClick={selectPlayer}
										selected={isSelected}
										disabled={selectionDisabled && !isSelected}
										teamColor={teamColor}
									/>
								</Grid>
							);
						})}
			</Grid>

			<Toolbar disableGutters>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					spacing={1}
					className={classes.gridContainer}
				>
					<Grid item>
						{players && !disableKeyListener && (
							<UniformKeyListener
								players={players}
								onMatch={selectPlayer}
								disabled={selectionDisabled}
								isOptional={optional}
								isEditing={editingPlay}
								selectedPlayers={selected}
							/>
						)}
					</Grid>

					<Grid item>
						<Grid container spacing={1}>
							{(canSkip() || canContinue()) && (
								<Grid item>
									<SecondaryButton
										variant="contained"
										size="medium"
										onClick={() => {
											selectPlayer({});
										}}
										label="Next"
									/>
								</Grid>
							)}
							{canClear() && (
								<Grid item>
									<SecondaryButton
										variant="contained"
										size="medium"
										onClick={clear}
										label="Clear"
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Toolbar>
		</>
	);
};

export default PlayerSelector;
